import * as React from 'react';
import { Link } from 'react-router-dom';

import { Box, Chip } from '@mui/material';

import dayjs from 'dayjs';
import { type MRT_ColumnDef } from 'material-react-table';

import { VenueReservationContentItemV2 } from '../../../graphql/API';
import Icons from '../../../themes/icons';
import Button from '../../Button/Button';
import { formatNumberWithCommas } from '../../../utils/formatter';

const EXPIRATION_DAYS = 90;

export type ContentTab = 'posts' | 'extra-content' | 'purchased';

type UseContentColumnsProps = {
  contentTab: ContentTab;
  onViewClick: (item: VenueReservationContentItemV2) => void;
  onDownloadClick?: (item: VenueReservationContentItemV2) => void;
};

const getPlatformInfo = (item: VenueReservationContentItemV2) => {
  if (item.postOnInstagramStories || item.postOnInstagramStories3x || item.postOnInstagramStories2x) {
    return { label: 'Story', Icon: Icons.Content1StoryIcon };
  }
  if (item.postOnInstagramFeed) {
    return { label: 'Feed', Icon: Icons.ContentFeedIcon };
  }
  if (item.postOnInstagramReels) {
    return { label: 'Reel', Icon: Icons.ContentReelIcon };
  }
  if (item.postOnTikTokVideo) {
    return { label: 'TikTok', Icon: Icons.TiktokIcon, isTikTok: true };
  }
  return null;
};

const formatDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

export const useContentColumns = ({
  contentTab,
  onViewClick,
  onDownloadClick,
}: UseContentColumnsProps): MRT_ColumnDef<VenueReservationContentItemV2>[] => {
  const contentColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'images.thumbnail',
    header: 'Content',
    size: 80,
    enableSorting: false,
    Cell: ({ row }) => (
      <Box
        component="img"
        src={row.original.images?.thumbnail ?? ''}
        alt=""
        sx={{
          width: 48,
          height: 48,
          borderRadius: '8px',
          objectFit: 'cover',
        }}
      />
    ),
  };

  const platformColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'platform',
    header: 'Platform',
    size: 130,
    enableSorting: false,
    Cell: ({ row }) => {
      const platformInfo = getPlatformInfo(row.original);
      if (!platformInfo) return <span>—</span>;
      const { label, Icon, isTikTok } = platformInfo;
      return (
        <Chip
          icon={
            isTikTok ? (
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ ml: '8px !important' }}>
                <Icon width={14} height={16} />
              </Box>
            ) : (
              <Icon style={{ width: 22, height: 22, marginLeft: 8 }} />
            )
          }
          label={label}
          variant="outlined"
          sx={{
            borderColor: '#E6E6E6',
            backgroundColor: '#FAFAFA',
            borderRadius: '8px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            fontSize: '15px',
            '& .MuiChip-icon': { marginRight: '-4px' },
          }}
        />
      );
    },
  };

  const creatorColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'user.firstname',
    header: 'Creator',
    size: 150,
    enableSorting: false,
    Cell: ({ row }) => {
      const { user } = row.original;
      const fullName = `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || '—';
      return (
        <Box
          component={Link}
          to={`/users/${user?.id}`}
          sx={{ color: '#111', textDecoration: 'none', ':hover': { textDecoration: 'underline' } }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {fullName}
        </Box>
      );
    },
  };

  const offersColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'offerTitle',
    header: 'Offer',
    size: 220,
    enableSorting: false,
    Cell: ({ row }) => (
      <Box
        component={Link}
        to={`/offers/${row.original.offerID}`}
        sx={{ color: '#111', textDecoration: 'none', ':hover': { textDecoration: 'underline' } }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {row.original.offerTitle || '—'}
      </Box>
    ),
  };

  const baseColumns: MRT_ColumnDef<VenueReservationContentItemV2>[] = [
    contentColumn,
    platformColumn,
    creatorColumn,
    offersColumn,
  ];

  const postedDateColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'createdAt',
    header: 'Posted date',
    size: 180,
    enableSorting: false,
    Cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
  };

  const uploadDateColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'createdAt',
    header: 'Upload date',
    size: 180,
    enableSorting: false,
    Cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
  };

  const priceColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'price',
    header: 'Price',
    size: 100,
    enableSorting: false,
    Cell: ({ row }) => (
      <span>${formatNumberWithCommas(row.original.price ?? 0)}</span>
    ),
  };

  const purchaseDateColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'downloadedAt',
    header: 'Purchase date',
    size: 180,
    enableSorting: false,
    Cell: ({ row }) => <span>{formatDate(row.original.downloadedAt)}</span>,
  };

  const usageRightsColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    id: 'usageRights',
    header: 'Usage rights',
    size: 120,
    enableSorting: false,
    Cell: ({ row }) => {
      const downloadedAt = row.original.downloadedAt;
      if (!downloadedAt) return <span>—</span>;

      const purchaseDay = dayjs(downloadedAt);
      const expirationDay = purchaseDay.add(EXPIRATION_DAYS, 'day');
      const remaining = expirationDay.diff(dayjs(), 'day');

      if (remaining <= 0) {
        return <span style={{ color: '#E53935', fontWeight: 500 }}>Expired</span>;
      }

      return <span>{remaining} days</span>;
    },
  };

  const actionColumn: MRT_ColumnDef<VenueReservationContentItemV2> = {
    accessorKey: 'id',
    header: 'Action',
    size: 140,
    enableSorting: false,
    Cell: ({ row }) => {
      const item = row.original;

      if (contentTab === 'posts') {
        if (item.postLink) {
          return (
            <Button
              variant="contained"
              size="small"
              component="a"
              href={item.postLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                backgroundColor: '#F9F9F9',
                fontWeight: 500,
                border: '1px solid #E0E0E0',
                color: '#111111',
                borderRadius: '8px',
                px: 2,
                '&:hover': { backgroundColor: '#d9d9d9' },
              }}
            >
              Go to post
            </Button>
          );
        }
        return null;
      }

      if (contentTab === 'extra-content') {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onViewClick(item);
            }}
            sx={{
              backgroundColor: '#F9F9F9',
              fontWeight: 500,
              border: '1px solid #E0E0E0',
              color: '#111111',
              borderRadius: '8px',
              px: 2,
              '&:hover': { backgroundColor: '#d9d9d9' },
            }}
          >
            Buy Content
          </Button>
        );
      }

      if (contentTab === 'purchased') {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onDownloadClick?.(item);
            }}
            sx={{
              backgroundColor: '#F9F9F9',
              fontWeight: 500,
              border: '1px solid #E0E0E0',
              color: '#111111',
              borderRadius: '8px',
              px: 2,
              '&:hover': { backgroundColor: '#d9d9d9' },
            }}
          >
            Download
          </Button>
        );
      }

      return null;
    },
  };

  if (contentTab === 'posts') {
    return [...baseColumns, postedDateColumn, actionColumn];
  }
  if (contentTab === 'extra-content') {
    return [...baseColumns, uploadDateColumn, priceColumn, actionColumn];
  }
  // purchased tab - no Platform column
  return [contentColumn, creatorColumn, offersColumn, uploadDateColumn, purchaseDateColumn, usageRightsColumn, actionColumn];
};
